import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { onAuthRequired } from "@/utils/auth";
import { authenticatedFetch } from "../utils/api";
import { API_URLS } from "../constants";
import { useModels } from "../hooks/useModels";
import { useAuth, useLanguage } from "../contexts";
import { AddPromptToCollectionModal } from "./collections";
import SaveNewVersionModal from "./SaveNewVersionModal";
import { NewPromptForm, PromptTabNavigation } from "./ui/prompt";
import { enhancePromptStreaming } from "@/utils/promptAgent";
import { useSmoothStream } from "@/hooks/useSmoothStream";
import { useNewPromptData } from "@/hooks/useNewPromptData";
import { usePrompt } from "../hooks/usePrompt";
import LoadingSkeleton from "./ui/prompt/detail/LoadingSkeleton";
import ErrorDisplay from "./ui/prompt/detail/ErrorDisplay";
import NotFoundDisplay from "./ui/prompt/detail/NotFoundDisplay";
import PromptHeader from "./ui/prompt/detail/PromptHeader";
import FloatingChatButton from "./ui/shared/FloatingChatButton";
import DiscoverTab from "./ui/prompt/detail/DiscoverTab";
import PlaygroundTab from "./ui/prompt/detail/PlaygroundTab";
import RedirectDisplay from "./ui/prompt/detail/RedirectDisplay";
import VersionsTab from "./ui/prompt/detail/VersionsTab";
import { LimitGuard } from "./LimitGuard";
import { useFeatureUsage } from "@/hooks/useUsageLimits";

interface PromptDetailProps {
  newPrompt?: boolean;
}

const PromptDetailComponent: React.FC<PromptDetailProps> = ({ newPrompt }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { models } = useModels();
  const { isLoggedIn, user } = useAuth();
  const { t } = useLanguage();
  
  // Check usage limits for private prompts
  const privatePromptsUsage = useFeatureUsage('private_prompts');
  const {
    prompt,
    setPrompt,
    latestVersion,
    loading,
    setLoading,
    error,
    setError,
    latestSystemPrompt,
    setLatestSystemPrompt,
    fetchPromptDetails,
  } = usePrompt(id);

  const [activeTab, setActiveTab] = useState<
    "current" | "playground" | "versions"
  >("current");
  const [showShare, setShowShare] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showAddToCollectionModal, setShowAddToCollectionModal] =
    useState(false);
  // Track visibility; initialize from prompt when it arrives
  const [isPublic, setIsPublic] = useState<boolean | undefined>(prompt?.is_public);
  useEffect(() => {
    if (prompt && prompt.is_public !== isPublic) {
      setIsPublic(prompt.is_public);
    }
  }, [prompt?.is_public, isPublic, prompt]);
  const [totalCopies, setTotalCopies] = useState<number>(0);
  const [promptInCollections, setPromptInCollections] = useState<Set<number>>(
    new Set()
  );
  const [isCheckingCollections, setIsCheckingCollections] = useState(false);

  const [playgroundData, setPlaygroundData] = useState({
    title: "",
    description: "",
    selectedModel: "",
    systemMessage: "",
    promptText: "",
    changeNotes: "",
    isSystemPromptExpanded: false,
    originalSystemMessage: "",
    enhancedSystemMessage: "",
  });
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasBeenEnhanced, setHasBeenEnhanced] = useState(false);
  const [originalPromptText, setOriginalPromptText] = useState("");
  const [enhancedPromptText, setEnhancedPromptText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const smoothEnabled = true;
  const {
    stream: smoothStreamText,
    addPart: smoothAddPart,
    reset: smoothReset,
  } = useSmoothStream({ charDelay: 8, mode: "char" });
  const playgroundPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const userScrolledAwayRef = useRef(false);

  const displayContent = {
    title:
      latestVersion?.title_display ||
      latestVersion?.title ||
      prompt?.title_display ||
      prompt?.title ||
      "",
    description:
      latestVersion?.description_display ||
      latestVersion?.description ||
      prompt?.description_display ||
      prompt?.description ||
      "",
    full_text:
      latestVersion?.full_text_display ||
      latestVersion?.full_text ||
      prompt?.full_text_display ||
      prompt?.full_text ||
      "",
    systemprompt:
      latestSystemPrompt ||
      latestVersion?.systemprompt ||
      prompt?.systemprompt_display ||
      prompt?.systemprompt ||
      "",
  };
  const isOwner = user?.id === prompt?.user_id;

  const handleSubmitPrompt = useCallback(
    async (data: {
      title: string;
      description: string;
      full_text: string;
      systemprompt: string;
      user: string;
      is_public?: boolean;
      model_type: string;
    }) => {
      try {
        const res = await authenticatedFetch(API_URLS.PROMPTS.CREATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          
          // Handle limit exceeded error specifically
          if (res.status === 403 && (errData.error?.code === 'LIMIT_EXCEEDED' || errData.error?.code === 'FORBIDDEN')) {
            const errorMessage = errData.error?.message || 
              errData.error?.details?.[0] || 
              t("limitExceeded") || 
              "You have reached your limit. Please upgrade your plan.";
            toast.error(errorMessage);
            // Dispatch event to refresh usage limits
            window.dispatchEvent(new CustomEvent('usageUpdated'));
            throw new Error(errorMessage);
          }
          
          // Handle both old and new error response formats
          const errorMessage =
            errData.error?.message ||
            errData.error ||
            errData.message ||
            t("failedCreatePrompt");
          throw new Error(errorMessage);
        }
        const newPromptResp = await res.json();
        if (newPromptResp?.id) {
          toast.success(t("promptSubmitted"));
          
          // Dispatch custom event to trigger usage limits refetch for both public and private prompts
          window.dispatchEvent(new CustomEvent('usageUpdated'));
          
          navigate(`/prompts/${newPromptResp.id}`);
        }
      } catch (e) {
        throw new Error(
          e instanceof Error ? e.message : t("failedCreatePrompt")
        );
      }
    },
    [navigate, t]
  );

  const {
    title,
    description,
    fullText,
    systemprompt: newSystemprompt,
    isPublic: newPromptIsPublic,
    selectedModel,
    isSystemPromptExpanded,
    isEnhanced: newPromptIsEnhanced,
    hasBeenEnhanced: newPromptHasBeenEnhanced,
    loading: newPromptLoading,
    isEnhancing: newPromptIsEnhancing,
    errors,
    apiError,
    actionButtonLabel,
    actionButtonDisabled,
    actionButtonTooltip,
    setTitle,
    setDescription,
    setFullText,
    setSystemprompt,
    setIsPublic: setNewPromptIsPublic,
    setSelectedModel,
    setIsSystemPromptExpanded,
    generateInitialFromTitleAndDescription,
    revertBoth,
    clearAll,
    handleEnhancePrompt,
  } = useNewPromptData({
    onSubmit: handleSubmitPrompt,
    onEnhancePrompt: async (p: string) => {
      let full = "";
      setFullText("");
      if (smoothEnabled) smoothReset();
      await enhancePromptStreaming(p, {
        onChunk: (c) => {
          const chunk = c.replace(/\\n/g, "\n");
          full += chunk;
          if (smoothEnabled) smoothAddPart(chunk);
          setFullText((prev) => prev + chunk);
        },
        onComplete: (f) => {
          if (f) full = f.replace(/\\n/g, "\n");
          setFullText(f ? f.replace(/\\n/g, "\n") : full);
        },
        onError: (err) => toast.error(err.message || t("failedEnhancePrompt")),
      });
      return full;
    },
  });

  const handlePlaygroundEnhancePrompt = useCallback(async () => {
    if (!playgroundData.promptText.trim()) {
      setEnhancementError(t("enhancedPromptError"));
      toast.error(t("enhancedPromptError"));
      return;
    }
    if (
      hasBeenEnhanced &&
      originalPromptText === playgroundData.promptText.trim() &&
      enhancedPromptText
    ) {
      setIsEnhanced(true);
      setPlaygroundData((p) => ({ ...p, promptText: enhancedPromptText }));
      return;
    }
    setIsEnhancing(true);
    setEnhancementError(null);
    smoothReset();
    setIsStreaming(true);
    try {
      setOriginalPromptText(playgroundData.promptText.trim());
      let enhanced = "";
      await enhancePromptStreaming(playgroundData.promptText, {
        onChunk: (chunk) => {
          const processed = chunk.replace(/\\n/g, "\n");
          enhanced += processed;
          if (smoothEnabled) smoothAddPart(processed);
          setPlaygroundData((p) => ({
            ...p,
            promptText: (p.promptText || "") + processed,
          }));
          const el = playgroundPromptRef.current;
          if (el && !userScrolledAwayRef.current) {
            const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
            if (dist < 160) el.scrollTop = el.scrollHeight;
          }
        },
        onComplete: (finalText) => {
          if (finalText) enhanced = finalText.replace(/\\n/g, "\n");
        },
        onError: (err) => {
          setEnhancementError(err.message || t("failedEnhancePrompt"));
          toast.error(err.message || t("failedEnhancePrompt"));
        },
      });
      setEnhancedPromptText(enhanced);
      setIsEnhanced(true);
      setHasBeenEnhanced(true);
      setPlaygroundData((p) => ({ ...p, promptText: enhanced }));
      toast.success(t("promptEnhancedSuccessfully"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("failedEnhancePrompt");
      setEnhancementError(msg);
      toast.error(msg);
    } finally {
      setIsEnhancing(false);
      setIsStreaming(false);
    }
  }, [
    playgroundData.promptText,
    hasBeenEnhanced,
    originalPromptText,
    enhancedPromptText,
    smoothEnabled,
    smoothAddPart,
    smoothReset,
    t,
  ]);
  const handleRevertToOriginal = useCallback(() => {
    setIsEnhanced(false);
    setEnhancementError(null);
    setPlaygroundData((p) => ({ ...p, promptText: originalPromptText }));
  }, [originalPromptText]);
  const handleToggleToEnhanced = useCallback(() => {
    if (hasBeenEnhanced && enhancedPromptText) {
      setIsEnhanced(true);
      setPlaygroundData((p) => ({ ...p, promptText: enhancedPromptText }));
    }
  }, [hasBeenEnhanced, enhancedPromptText]);

  useEffect(() => {
    if (prompt && !newPrompt) {
      setPlaygroundData((p) => ({
        ...p,
        promptText: (
          latestVersion?.full_text ||
          prompt.full_text ||
          ""
        ).replace(/\\n/g, "\n"),
      }));
    }
  }, [prompt, latestVersion, newPrompt]);

  const checkPromptCollections = useCallback(async () => {
    if (!id || !isLoggedIn) return;
    try {
      setIsCheckingCollections(true);
      const collectionsRes = await authenticatedFetch("/api/collections");
      if (!collectionsRes.ok) throw new Error("Failed to fetch collections");
      const data = await collectionsRes.json();
      const userCollections = data.data || [];
      const withPrompt = new Set<number>();
      for (const c of userCollections) {
        try {
          const cr = await authenticatedFetch(
            `/api/collections/${c.id}/prompts`
          );
          if (cr.ok) {
            const pd = await cr.json();
            const prompts = pd.data || [];
            if (prompts.some((p: { id: number }) => p.id === parseInt(id)))
              withPrompt.add(c.id);
          }
        } catch (err) {
          console.error(`Error checking collection ${c.id}:`, err);
        }
      }
      setPromptInCollections(withPrompt);
    } catch (err) {
      console.error("Error checking prompt collections:", err);
    } finally {
      setIsCheckingCollections(false);
    }
  }, [id, isLoggedIn]);

  useEffect(() => {
    if (!newPrompt) fetchPromptDetails();
  }, [fetchPromptDetails, newPrompt]);
  useEffect(() => {
    if (!newPrompt) checkPromptCollections();
  }, [checkPromptCollections, newPrompt]);
  useEffect(() => {
    if (newPrompt) {
      setLoading(false);
      setActiveTab("playground");
      setPrompt({
        id: "",
        category_id: 0,
        created_at: "",
        description: "",
        full_text: "",
        is_public: false,
        model_type: "",
        modified_at: "",
        posted_at: "",
        title: "",
        updated_at: "",
        user_id: "",
        views: 0,
        votes: 0,
        total_views: 0,
        total_copies: 0,
        prompt_type: "user",
      });
    }
  }, [newPrompt, setLoading, setPrompt]);
  useEffect(() => {
    if (models.length > 0 && !playgroundData.selectedModel) {
      const def =
        prompt?.model_type && models.find((m) => m.name === prompt.model_type)
          ? prompt!.model_type
          : models[0].name;
      setPlaygroundData((p) => ({ ...p, selectedModel: def }));
    }
  }, [models, playgroundData.selectedModel, prompt?.model_type, prompt]);
  useEffect(() => {
    if (prompt) setIsPublic(prompt.is_public);
  }, [prompt]);
  useEffect(() => {
    if (activeTab !== "playground") {
      setIsEnhanced(false);
      setHasBeenEnhanced(false);
      setIsEnhancing(false);
      setEnhancementError(null);
      setOriginalPromptText("");
      setEnhancedPromptText("");
    }
  }, [activeTab]);

  const handleBack = () => navigate(-1);
  const handleShare = () => setShowShare((s) => !s);
  const saveCopyEvent = async () => {
    try {
      await fetch(API_URLS.ANALYTICS.PROMPT_EVENT, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          promptId: id,
          userId: user?.id,
          eventType: "copy",
        }),
      });
      fetchPromptCopies();
    } catch (err) {
      console.error(`${t("failedToRegisterEvent")}:`, err);
      toast.error(t("failedToRegister"));
    }
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/prompts/${prompt?.id}`
      );
      toast.success(t("copiedLink"));
      setShowShare(false);
    } catch (err) {
      console.error(`${t("failedToCopy")}:`, err);
      toast.error(`${t("failedToCopy")} link`);
    }
  };
  const handleCopyText = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      saveCopyEvent();
      toast.success(`${type} ${t("copiedToClipboard")}`);
    } catch (err) {
      console.error(`${t("failedToCopy")}:`, err);
      toast.error(`${t("failedToCopy")} ${type.toLowerCase()}`);
    }
  };
  const fetchPromptCopies = useCallback(async () => {
    try {
      if (prompt) {
        // Use authenticatedFetch so language (?lang=) & X-User-Language header are sent
        const r = await authenticatedFetch(
          API_URLS.ANALYTICS.PROMPT_EVENT_DETAIL(Number(prompt.id))
        );
        if (r.ok) {
          const d = await r.json();
          setTotalCopies(d.totalCopies);
        }
      }
    } catch (err) {
      console.error(t("errorFetchingPromptCopies"), err);
      setError(t("failedLoadPromptCopies"));
    }
  }, [prompt, t, setError]);
  useEffect(() => {
    if (!newPrompt) fetchPromptCopies();
  }, [fetchPromptCopies, newPrompt]);
  const togglePromptVisibility = async () => {
    try {
      if (prompt) {
        // Check if changing from public to private
        const willBecomePrivate = prompt.is_public === true;
        
        // If changing to private, check if user has capacity
        if (willBecomePrivate && privatePromptsUsage && !privatePromptsUsage.hasCapacity) {
          // Show error - user has exceeded limit
          toast.error(
            t("limitExceeded") || 
            `You have reached your limit of ${privatePromptsUsage.limit_value} private prompts. Please upgrade your plan.`
          );
          return;
        }
        
        const r = await authenticatedFetch(
          API_URLS.PROMPTS.TOGGLE_VISIBILITY(Number(prompt.id)),
          { method: "PATCH" }
        );
        if (r.ok) {
          const upd = await r.json();
          const vis = upd.prompt.isPublic;
          setPrompt((prev) => ({ ...prev!, is_public: vis }));
          setIsPublic(vis);
          toast.success(
            `${t("promptNow")} ${vis ? t("public") : t("private")}`
          );
          
          // Dispatch custom event to trigger usage limits refetch
          window.dispatchEvent(new CustomEvent('usageUpdated'));
        } else {
          const errorData = await r.json().catch(() => ({}));
          
          // Handle limit exceeded error from backend
          if (r.status === 403 && (errorData.error?.code === 'LIMIT_EXCEEDED' || errorData.error?.code === 'FORBIDDEN')) {
            const errorMessage = errorData.error?.message || 
              errorData.error?.details?.[0] || 
              t("limitExceeded") || 
              "You have reached your limit. Please upgrade your plan.";
            toast.error(errorMessage);
            // Dispatch event to refresh usage limits
            window.dispatchEvent(new CustomEvent('usageUpdated'));
            return;
          }
          
          toast.error(t("failedToggleVisibility"));
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(t("failedUpdateVisibility"));
    }
  };
  const handleActionClick = async () => {
    if (
      actionButtonLabel === t("generateInitialPrompt") ||
      actionButtonLabel === "Generate Initial Prompt"
    )
      await generateInitialFromTitleAndDescription();
    else handleEnhancePrompt();
  };
  const handlePromptSubmit = () => {
    if (!newPrompt) {
      if (!playgroundData.promptText.trim()) {
        toast.error(t("promptNotEmpty"));
        return;
      }
      setShowSaveModal(true);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorDisplay error={error} onBack={handleBack} />;
  // Only decide private redirect after prompt loaded (avoid early falsey isPublic triggering redirect)
  if (!loading && prompt && isPublic === false && !isOwner && !newPrompt) {
    return <RedirectDisplay />;
  }
  if (newPrompt) {
    // Determine which feature to guard based on isPublic selection
    const featureToGuard = (newPromptIsPublic ?? true) ? 'public_prompts' : 'private_prompts';
    
    return (
      <div
        className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-6xl"
        data-testid="prompt-detail"
      >
        <LimitGuard feature={featureToGuard}>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-xl dark:shadow-slate-900/50 transition-colors duration-300">
            <PromptHeader
              title=""
              isNewPrompt={true}
              latestVersion={null}
              promptId=""
              showShare={false}
              onToggleShare={() => {}}
              onCopyLink={() => {}}
            />
            <div className="p-6 bg-white dark:bg-slate-800 transition-colors duration-300">
              <NewPromptForm
                title={title}
                description={description}
                fullText={
                  smoothEnabled && newPromptIsEnhancing
                    ? smoothStreamText || fullText
                    : fullText
                }
                systemprompt={newSystemprompt}
                isPublic={newPromptIsPublic ?? true}
                selectedModel={selectedModel}
                isSystemPromptExpanded={isSystemPromptExpanded}
                isEnhanced={newPromptIsEnhanced}
                hasBeenEnhanced={newPromptHasBeenEnhanced}
                loading={newPromptLoading}
                isEnhancing={newPromptIsEnhancing}
                errors={errors}
                apiError={apiError}
                error={error}
                actionButtonLabel={actionButtonLabel}
                actionButtonDisabled={actionButtonDisabled}
                actionButtonTooltip={actionButtonTooltip}
                setTitle={setTitle}
                setDescription={setDescription}
                setFullText={setFullText}
                setSystemprompt={setSystemprompt}
                setIsPublic={
                  setNewPromptIsPublic as React.Dispatch<
                    React.SetStateAction<boolean>
                  >
                }
                setSelectedModel={setSelectedModel}
                setIsSystemPromptExpanded={setIsSystemPromptExpanded}
                onActionClick={handleActionClick}
                onRevertClick={revertBoth}
                onClearClick={clearAll}
                onSubmit={async (d) => {
                  await handleSubmitPrompt({ ...d, user: user?.id || "" });
                }}
              />
            </div>
          </div>
        </LimitGuard>
      </div>
    );
  }
  if (!prompt) return <NotFoundDisplay onBack={handleBack} />;

  return (
    <div
      className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-6xl"
      data-testid="prompt-detail"
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-xl dark:shadow-slate-900/50 transition-colors duration-300">
        <PromptHeader
          title={displayContent.title}
          isNewPrompt={!!newPrompt}
          latestVersion={latestVersion}
          promptId={prompt.id}
          showShare={showShare}
          onToggleShare={handleShare}
          onCopyLink={handleCopy}
        />
        <PromptTabNavigation
          isLoggedIn={isLoggedIn}
          activeTab={activeTab}
          prompt={prompt}
          newPrompt={newPrompt}
          setActiveTab={setActiveTab}
          setShowAddToCollectionModal={setShowAddToCollectionModal}
          promptInCollections={promptInCollections}
          isCheckingCollections={isCheckingCollections}
        />
        <div className="flex flex-col lg:flex-row max-w-6xl gap-4 lg:gap-0 bg-white dark:bg-slate-800 transition-colors duration-300">
          {activeTab === "current" && (
            <DiscoverTab
              displayContent={{
                title: displayContent.title,
                description: displayContent.description,
                full_text: displayContent.full_text,
                systemprompt: displayContent.systemprompt,
              }}
              latestVersion={latestVersion}
              prompt={prompt}
              models={models}
              promptId={prompt.id}
              isLoggedIn={isLoggedIn}
              currentUserId={user?.id}
              onAuthRequired={onAuthRequired}
              onCopyText={handleCopyText}
              totalCopies={totalCopies}
              isOwner={isOwner}
              isPublic={isPublic ?? false}
              onToggleVisibility={togglePromptVisibility}
              showComments={!newPrompt}
            />
          )}
          {activeTab === "playground" && (
            <PlaygroundTab
              newPrompt={newPrompt}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              playgroundData={playgroundData as any}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setPlaygroundData={setPlaygroundData as any}
              isEnhanced={isEnhanced}
              hasBeenEnhanced={hasBeenEnhanced}
              isEnhancing={isEnhancing}
              isStreaming={isStreaming}
              smoothEnabled={smoothEnabled}
              smoothStreamText={smoothStreamText}
              handlePlaygroundEnhancePrompt={handlePlaygroundEnhancePrompt}
              handleRevertToOriginal={handleRevertToOriginal}
              handleToggleToEnhanced={handleToggleToEnhanced}
              handlePromptSubmit={handlePromptSubmit}
              enhancedPromptText={enhancedPromptText}
              enhancementError={enhancementError}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              models={models as any}
              latestVersion={latestVersion}
              displayContent={{
                title: displayContent.title,
                description: displayContent.description,
                full_text: displayContent.full_text,
                systemprompt: displayContent.systemprompt,
              }}
              smoothReset={smoothReset}
              playgroundPromptRef={playgroundPromptRef}
              userScrolledAwayRef={userScrolledAwayRef}
              onCopyText={handleCopyText}
            />
          )}
          {activeTab === "versions" && <VersionsTab promptId={id!} />}
        </div>
      </div>
      <FloatingChatButton />
      <SaveNewVersionModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        promptId={id!}
        promptTitle={playgroundData.title || prompt?.title || ""}
        promptDescription={playgroundData.description || prompt?.description || ""}
        playgroundSystemMessage={playgroundData.systemMessage}
        playgroundPromptText={playgroundData.promptText}
        playgroundChangeNotes={playgroundData.changeNotes}
        onSuccess={async () => {
          setShowSaveModal(false);
          setIsEnhanced(false);
          setHasBeenEnhanced(false);
          setIsEnhancing(false);
          setEnhancementError(null);
          setOriginalPromptText("");
          setEnhancedPromptText("");
          setPlaygroundData((p) => ({
            ...p,
            originalSystemMessage: "",
            enhancedSystemMessage: "",
          }));
          await fetchPromptDetails();
          setLatestSystemPrompt(playgroundData.systemMessage || "");
          setActiveTab("current");
        }}
      />
      {showAddToCollectionModal && (
        <AddPromptToCollectionModal
          promptId={id!}
          onClose={() => setShowAddToCollectionModal(false)}
          onSuccess={() => {
            setShowAddToCollectionModal(false);
            toast.success("Prompt Collections updated successfully!");
          }}
        />
      )}
    </div>
  );
};

export const PromptDetail = React.memo(PromptDetailComponent);
export default PromptDetail;
